import Hero from "@/components/hero/hero";
import { Flex, Typography } from "antd";
import TPremiaCard from "@/components/t-premia-card/t-premia-card";
import OverflowComponent from "@/components/overflow-container/overflow-container";
import { CampaignCard } from "@/components/campaign-card/campaign-card";
import { GridCards } from "@/components/grid-cards/grid-cards";
import { ContentSection, ContentWithMaxWidth, LoaderContainer, SizeOfMenu, TextDescription, TextTitle } from "./style-component";
import { getCampaign } from "@/services/campaign/campaign";
import { getBenefits } from "@/services/benefits/benefit";
import useFetch from "@/hook/useFetch/useFetch";
import { getMissionCustom } from "@/services/mission/missions";
import { SpaceshipLoader } from "@/components/spaceship-loader/spaceship-loader";
import { Campaign } from "@/types/campaign/campaign-type";
import SEOHelmet from "@/components/SEOHelmet/SEOHelmet";
import brandData from "@/assets/brand.json"
import { useUserContext } from "@/contexts/user/useUserContext";
import { useEffect, useRef, useState } from "react";
import OnboardingModal from "@/components/onboarding-modal/onboarding-modal";
import { amplitudeAddTrack, determineProjectName } from "@/utils/amplitud/event-track";
import DOMPurify from 'dompurify';

const LoggedPage: React.FC = () => {
  const { isConnect } = useUserContext();

  const homeConfig = (() => {
    try {
      const configString = import.meta.env.VITE_HOME_CONFIG;
      if (!configString) return {};
      return JSON.parse(configString);
    } catch (error) {
      console.warn('Failed to parse home config from this enviroment:', error);
      return {};
    }
  })();

  const {
    data: campaigns,
    loading: campaignsLoading,
    error: campaignsError,
  } = useFetch(getCampaign, { limit: 3, page: 0, order: 'start_date_asc' });

  const {
    data: availableBenefits,
    loading: benefitsLoading,
    error: benefitsError
  } = useFetch(getBenefits, { limit: 4 });

  const {
    data: activeMissions,
    loading: missionsLoading,
    error: missionsError,
    startFetch: startFetchMission
  } = useFetch(getMissionCustom, { limit: 4, order: 'start_date_asc' });
  const existError = campaignsError && missionsError && benefitsError;
  const [missionState, setMissionState] = useState(activeMissions ?? {});

  const firstRender = useRef<boolean>(true);
  useEffect(() => {
    if (!missionsLoading && isConnect && Object.keys(activeMissions)?.length === 0 && firstRender.current) {
      firstRender.current = false;
      startFetchMission();
    }
  }, [missionsLoading, isConnect, activeMissions, startFetchMission]);

  useEffect(() => {
    setMissionState(activeMissions ?? {});
  }, [activeMissions]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      amplitudeAddTrack(determineProjectName('Home'), {
        title: determineProjectName('Home'),
      })
    }
  }, []);

  return (
    <ContentSection>
      <SEOHelmet
        metadata={{
          title:  determineProjectName('Home'),
          description: 'Bienvenidos a la expericiencia web3',
        }}
      />
      <TPremiaCard variant="gradientPurple" style={{ minHeight: 'auto', padding: '0px', margin: '0px', boxShadow: 'none', borderRadius: '0px' }}>
        <SizeOfMenu />
        <Hero
          webpSrc={brandData.pages.home.hero.image}
          fallbackSrc={brandData.pages.home.hero.fallback}
          alt="Hero illustration"
          title={<TextTitle dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(brandData.pages.home.hero.heading) }} />}
          description={
            <Flex vertical gap={16}>
              <TextDescription
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(brandData.pages.home.hero.subheading) }}
              />
              {import.meta.env.VITE_SHOW_ENTER_SITE_MODAL === "true" &&
                <OnboardingModal rediectTo={`/campaign/${campaigns[0]?.id}`}/>
              }
            </Flex>
          }
        />
      </TPremiaCard>
      <div style={{ padding: '0px 12px 96px 12px' }}>
        {!existError ? (
          <ContentWithMaxWidth>
            <TPremiaCard
              variant="dark"
              title={homeConfig?.campaigns?.title || "Campañas"}
              style={{
                padding: '32px',
                display: (homeConfig?.campaigns?.visible ?? true) ? '' : 'none'
              }}
            >
              {campaignsLoading ? (
                <LoaderContainer>
                  <SpaceshipLoader height="150px" />
                </LoaderContainer>
              ) : (
                <div style={{display: "grid", justifyContent: "center"}}>
                  <GridCards
                    items={Array.isArray(campaigns) ? campaigns?.slice(0, 3) : []}
                    variant="campaignOverflow"
                    renderItem={(campaign: Campaign, index) => (
                      <CampaignCard
                        data-testid="campaign-card"
                        highlight={index === 0}
                        id={campaign.id}
                        title={campaign.name}
                        imageUrl={campaign.banner_url}
                        end_date={campaign.end_date}
                        start_date={campaign.start_date}
                      />
                    )}
                  />
                </div>
              )}
            </TPremiaCard>
            <TPremiaCard
              variant="dark"
              title={homeConfig?.missions?.title || "Misiones"}
              style={{
                padding: '32px',
                display: (homeConfig?.missions?.visible ?? true) ? '' : 'none'
              }}
            >
              {missionsLoading && Object.keys(missionState)?.length == 0 ? (
                <LoaderContainer>
                  <SpaceshipLoader height="100px" />
                </LoaderContainer>
              ) : (
                <OverflowComponent items={missionState} />
              )}

            </TPremiaCard>
            <TPremiaCard
              variant="blueGradient"
              title={homeConfig?.benefits?.title || "Beneficios"}
              style={{
                padding: '32px',
                display: (homeConfig?.benefits?.visible ?? true) ? '' : 'none'
              }}
            >
              {benefitsLoading ? (
                <LoaderContainer>
                  <SpaceshipLoader height="100px" />
                </LoaderContainer>
              ) : (
                <OverflowComponent
                  items={availableBenefits}
                  isBenefit={true}
                />
              )}
            </TPremiaCard>

          </ContentWithMaxWidth>
        ) : (
          <Typography style={{ textAlign: 'center', padding: '2rem', minHeight: '100%', flex: 1, alignSelf: 'stretch' }}>
            Error al cargar los datos. Por favor, intente nuevamente.
          </Typography>
        )}
      </div>
    </ContentSection>
  );
};

export default LoggedPage;